import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, Camera } from "lucide-react";
import { api, resolveImage } from "../lib/api";

/**
 * Extracts a YouTube video id from any URL (or returns the input if already an id).
 */
function youtubeId(input) {
  if (!input) return "";
  const s = String(input).trim();
  if (/^[\w-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/);
  return m ? m[1] : s;
}

function vimeoId(input) {
  if (!input) return "";
  const s = String(input).trim();
  if (/^\d+$/.test(s)) return s;
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : s;
}

function MediaSlide({ item, isActive }) {
  if (!item) return null;
  const { kind, source, url, title, subtitle, thumbnail } = item;

  if (kind === "image") {
    return (
      <img
        src={resolveImage(url)}
        alt={title || "Pextlán"}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
        loading="lazy"
      />
    );
  }

  // VIDEO
  if (source === "youtube") {
    const id = youtubeId(url);
    return (
      <iframe
        key={isActive ? `yt-on-${id}` : `yt-off-${id}`}
        title={title || `youtube-${id}`}
        src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1${isActive ? "&autoplay=1&mute=1" : ""}`}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (source === "vimeo") {
    const id = vimeoId(url);
    return (
      <iframe
        key={isActive ? `vm-on-${id}` : `vm-off-${id}`}
        title={title || `vimeo-${id}`}
        src={`https://player.vimeo.com/video/${id}?autoplay=${isActive ? 1 : 0}&muted=1`}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  // upload / url -> native HTML5 video
  return (
    <video
      key={`v-${url}-${isActive}`}
      src={resolveImage(url)}
      poster={thumbnail ? resolveImage(thumbnail) : undefined}
      className="absolute inset-0 w-full h-full object-cover"
      controls
      playsInline
      muted
      autoPlay={isActive}
      loop={isActive}
    />
  );
}

export default function MediaCarousel({ content = {} }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/media")
      .then((res) => {
        if (cancelled) return;
        const sorted = (res.data || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setItems(sorted);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't render the section if empty (admin hasn't added any media yet).
  if (!loading && items.length === 0) return null;

  const total = items.length;
  const current = items[idx];
  const next = () => setIdx((i) => (i + 1) % Math.max(1, total));
  const prev = () => setIdx((i) => (i - 1 + Math.max(1, total)) % Math.max(1, total));

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  return (
    <section
      id="galeria"
      data-testid="gallery-section"
      className="relative py-28 bg-gradient-to-b from-[#0B1A0F] via-[#102614] to-[#0B1A0F] overflow-hidden"
    >
      <div className="orb w-[420px] h-[420px] bg-[#F1C36B]/25 -top-32 -right-24" />
      <div className="orb w-[380px] h-[380px] bg-[#B8E07A]/25 -bottom-32 -left-24" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <p className="text-[10px] tracking-[0.4em] uppercase text-[var(--c-firefly-gold)] mb-4">
            <Camera className="inline w-3 h-3 mr-2 -mt-0.5" />
            {content.gallery_overline || "Imágenes y videos"}
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-[var(--c-text)] leading-tight">
            {content.gallery_title_1 || "Un vistazo a"}{" "}
            <span className="italic text-[var(--c-firefly-gold)] font-light">
              {content.gallery_title_fairy || "Pextlán en movimiento"}
            </span>
          </h2>
          {content.gallery_description ? (
            <p className="mt-5 text-[var(--c-text)]/70 leading-relaxed">
              {content.gallery_description}
            </p>
          ) : null}
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div
            className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden bg-[var(--c-bg-forest)] border border-[var(--c-bio-green)]/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="gallery-stage"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={current?.id || idx}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <MediaSlide item={current} isActive />
              </motion.div>
            </AnimatePresence>

            {/* Overlay caption */}
            {(current?.title || current?.subtitle) && current?.kind === "image" && (
              <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent px-6 sm:px-10 py-6 sm:py-8 pointer-events-none">
                {current.title && (
                  <p className="font-serif text-xl sm:text-2xl text-white drop-shadow">
                    {current.title}
                  </p>
                )}
                {current.subtitle && (
                  <p className="mt-1 text-sm text-white/80 max-w-2xl">{current.subtitle}</p>
                )}
              </div>
            )}

            {/* Kind badge */}
            {current?.kind === "video" && (
              <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-black/55 backdrop-blur px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] text-white pointer-events-none">
                <Play className="w-3 h-3 fill-current" />
                Video
              </div>
            )}

            {/* Arrows */}
            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Anterior"
                  data-testid="gallery-prev"
                  className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-[var(--c-firefly-gold)] hover:text-[#0B1A0F] text-white backdrop-blur grid place-items-center transition-colors border border-white/15"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Siguiente"
                  data-testid="gallery-next"
                  className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-[var(--c-firefly-gold)] hover:text-[#0B1A0F] text-white backdrop-blur grid place-items-center transition-colors border border-white/15"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails / dots */}
          {total > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              {items.map((m, i) => {
                const active = i === idx;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setIdx(i)}
                    data-testid={`gallery-thumb-${i}`}
                    aria-label={m.title || `Slide ${i + 1}`}
                    className={`relative h-14 w-20 sm:h-16 sm:w-24 rounded-xl overflow-hidden border transition-all ${
                      active
                        ? "border-[var(--c-firefly-gold)] scale-105 shadow-[0_8px_30px_-8px_var(--c-firefly-gold)]"
                        : "border-[var(--c-bio-green)]/25 opacity-70 hover:opacity-100"
                    }`}
                  >
                    {m.kind === "image" ? (
                      <img
                        src={resolveImage(m.url)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : m.thumbnail ? (
                      <img
                        src={resolveImage(m.thumbnail)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-[var(--c-bg-deep)] grid place-items-center">
                        <Play className="w-4 h-4 text-white/80 fill-current" />
                      </div>
                    )}
                    {m.kind === "video" && (
                      <div className="absolute inset-0 grid place-items-center bg-black/30">
                        <Play className="w-4 h-4 text-white fill-current" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
